import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setCheckOutConfirmIsOpen } from "redux/checkOutConfirmInfoSlice";
import ModalCheckOutConfirm from "screens/ModalCheckOutConfirm";
import { Helmet } from "react-helmet";
import FooterBar from "components/FooterBar";
import Spinner from "components/Spinner";
import FloorCard from "./components/FloorCard";
import Alerts from "./components/Alerts";
import Messages from "./components/Messages";
import { useQuery } from "@apollo/client";
import { GET_ALL_ROOMS, SUBSCRIBE_TO_ROOMS_UPDATED } from "graphql/service";
import { Content, ContentColumn, Container } from "./styles";
import { debouncedErrorMessage } from "utils/messageUtil";

const Home = () => {
  const dispatch = useDispatch();
  const [eighthFloorRooms, setEighthFloorRooms] = useState({});
  const [ninthFloorRooms, setNinthFloorRooms] = useState({});
  const [tenthFloorRooms, setTenthFloorRooms] = useState({});

  const checkOutConfirmInfo = useSelector(
    (state) => state.checkOutConfirmInfoReducer
  );

  const {
    loading,
    data: roomsData,
    error,
    subscribeToMore,
  } = useQuery(GET_ALL_ROOMS, {
    fetchPolicy: "cache-first",
    onCompleted: (roomsData) => {
      let newEighthFloorRooms = {};
      let newNinthFloorRooms = {};
      let newTenthFloorRooms = {};
      roomsData.validRooms.forEach((room) => {
        switch (room.floor) {
          case "8":
            newEighthFloorRooms[room.roomNumber] = room;
            break;
          case "9":
            newNinthFloorRooms[room.roomNumber] = room;
            break;
          case "10":
            newTenthFloorRooms[room.roomNumber] = room;
            break;
          default:
            break;
        }
      });
      setEighthFloorRooms(newEighthFloorRooms);
      setNinthFloorRooms(newNinthFloorRooms);
      setTenthFloorRooms(newTenthFloorRooms);
    },
    onError: (error) => {
      if (!error.networkError) {
        let content = error.message;
        if (error.message.includes("Validation")) {
          content = JSON.stringify(
            error.graphQLErrors[0]?.extensions?.exception
          );
        }
        debouncedErrorMessage(content);
      }
    },
  });

  useEffect(() => {
    if (!roomsData?.validRooms) return;

    const newEighthFloorRooms = {};
    const newNinthFloorRooms = {};
    const newTenthFloorRooms = {};

    roomsData.validRooms.forEach((room) => {
      switch (room.floor) {
        case "8":
          newEighthFloorRooms[room.roomNumber] = room;
          break;
        case "9":
          newNinthFloorRooms[room.roomNumber] = room;
          break;
        case "10":
          newTenthFloorRooms[room.roomNumber] = room;
          break;
        default:
          break;
      }
    });

    setEighthFloorRooms(newEighthFloorRooms);
    setNinthFloorRooms(newNinthFloorRooms);
    setTenthFloorRooms(newTenthFloorRooms);
  }, [roomsData?.validRooms]);

  useEffect(() => {
    // This assumes you want to wait to start the subscription
    // after the query has loaded.
    if (roomsData) {
      const unsubscribe = subscribeToMore({
        document: SUBSCRIBE_TO_ROOMS_UPDATED,
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data?.roomMapUpdated) return;
          const updatedRooms = subscriptionData.data?.roomMapUpdated;

          const mergedRooms = prev.validRooms.map((room) => {
            const updated = updatedRooms.find(
              (element) => element.roomId === room.roomId
            );
            return updated ? updated : room;
          });
          if (
            prev.validRooms?.some(
              (room) => room.roomId === updatedRooms.alertId
            )
          ) {
            return prev;
          }
          return {
            ...prev,
            validRooms: [...mergedRooms],
          };
        },
      });

      return () => {
        unsubscribe();
      };
    }
  }, [roomsData, subscribeToMore]);

  if (loading) return <Spinner />;
  if (error) return <p>Error: {error.message}</p>;

  return (
    <>
      <Helmet>
        <title>Home - Rook Hotel PMS</title>
        <meta name="description" content="Home - Rook Hotel PMS" />
      </Helmet>
      <Content>
        <ContentColumn>
          <Container>
            <FloorCard
              eighthFloorRooms={eighthFloorRooms}
              ninthFloorRooms={ninthFloorRooms}
              tenthFloorRooms={tenthFloorRooms}
            />
            <ModalCheckOutConfirm
              isOpen={checkOutConfirmInfo.checkOutConfirmIsOpen}
              setIsOpen={(e) => dispatch(setCheckOutConfirmIsOpen(e))}
              roomString={"room " + checkOutConfirmInfo.roomNumber}
              checkOutBookings={checkOutConfirmInfo.checkOutBookings}
            />
          </Container>
        </ContentColumn>
        <ContentColumn desktop>
          <Container>
            <Alerts />
          </Container>
        </ContentColumn>
        <ContentColumn desktop>
          <Container>
            <Messages />
          </Container>
        </ContentColumn>
      </Content>
      <FooterBar />
    </>
  );
};

export default Home;
